import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

// Real PatientProfile shape:
//   { id, user_id, username, full_name, email, date_of_birth, sex,
//     phone_number, place_of_residence, patient_category,
//     emergency_contact_name, emergency_contact_phone, created_at, updated_at }
//
// The UI historically used { full_name, mrn, dob, sex, phone, province }.
// We add compatibility aliases so older screens still render.
//
// MRN (medical record number) format: "WTK-NNNNNN". Derived from the
// PatientProfile PK so every patient has a stable identifier without
// requiring a separate MRN column in the DB today. If we ever swap in a
// real government-issued UID (PNG NID, say), this is the one place to
// change it and every screen will pick up the new format.
function formatMrn(id) {
  if (id == null) return ''
  const n = String(id).padStart(6, '0')
  return `WTK-${n}`
}

function adaptPatient(p) {
  if (!p) return p
  return {
    ...p,
    mrn: formatMrn(p.id),
    dob: p.date_of_birth,
    phone: p.phone_number,
    province: p.place_of_residence,
  }
}

export { formatMrn }

export async function listPatients({ q, limit = 50 } = {}) {
  const params = { page_size: limit }
  if (q) params.search = q
  const { data } = await api.get(endpoints.patientList, { params })
  return unwrapList(data).map(adaptPatient)
}

export async function getPatient(id) {
  const { data } = await api.get(endpoints.patientDetail(id))
  return adaptPatient(data)
}

/**
 * Register a new patient.
 *
 * Doctors and admins are allowed to call this. The backend provisions a
 * backing auth User row (username derived from first+last if not given).
 * Unused fields can be left blank.
 *
 * @param {object} data
 * @param {string} data.first_name
 * @param {string} data.last_name
 * @param {string=} data.username_in     Optional custom username.
 * @param {string=} data.email_in        Optional email.
 * @param {string=} data.password        If blank, account gets unusable password.
 * @param {string}  data.sex             'male' | 'female' | 'other'
 * @param {string=} data.date_of_birth   ISO date, e.g. '1990-05-14'.
 * @param {string=} data.phone_number
 * @param {string=} data.place_of_residence
 * @param {string=} data.patient_category  Default 'general'.
 * @param {string=} data.emergency_contact_name
 * @param {string=} data.emergency_contact_phone
 */
export async function createPatient(data) {
  const { data: created } = await api.post(endpoints.patientList, data)
  return adaptPatient(created)
}
